<template>
    <BaseFormField
        class="event-reservation-button"
        :errors="[errorMessage]"
    >
        <template v-if="confirm">
            <BaseButton
                type="button"
                class="button--secondary"
                :disabled="isSubmitting"
                @click="toggleReservation"
            >
                {{ buttonConfirmationText }}
            </BaseButton>

            <div />

            <BaseButton
                type="button"
                class="button"
                style="margin-top: .5rem;"
                :disabled="isSubmitting"
                @click="$emit('submitted'); confirm = false"
            >
                Annuleren
            </BaseButton>
        </template>

        <BaseButton
            v-if="hasReservation"
            type="button"
            class="button--secondary"
            :disabled="isSubmitting"
            @click="toggleReservation"
        >
            Reservering annuleren
        </BaseButton>

        <BaseButton
            v-else-if="!confirm"
            type="button"
            class="button--secondary"
            :disabled="isSubmitting"
            @click="$emit('needsConfirmation'); confirm = true"
        >
            {{ buttonText }}
        </BaseButton>
    </BaseFormField>
</template>

<script setup lang="ts">
import { STATUS, EVENT_FULL_CAPACITY } from '~/constants/errorMessages';

const props = defineProps<{
    event: EventDate
}>();

const emit = defineEmits(['submitted', 'needsConfirmation']);

const isSubmitting = ref(false);
const errorMessage = ref<string|null>(null);
const confirm = ref(false);

const userStore = useUserStore();
const eventsStore = useEventsStore();

const { reservations } = storeToRefs(userStore);

const hasReservation = computed(() => reservations.value.includes(props.event.id));
const ticketPricing = computed(() => eventsStore.ticketPricing);

const buttonText = computed(() => {
    if (!ticketPricing.value.needsPayment || props.event.partnerCollectsAdditionalFee) {
        return 'Direct reserveren';
    }

    if (ticketPricing.value.parsedPrice) {
        return 'Reserveer direct voor €' + ticketPricing.value.parsedPrice;
    }

    return 'Direct reserveren';
});

const buttonConfirmationText = computed(() => {
    if (
        !props.event.partnerCollectsAdditionalFee
        && ticketPricing.value.needsPayment
        && ticketPricing.value.parsedPrice
    ) {
        return 'Bevestig. Incasso €' + ticketPricing.value.parsedPrice + ' na bezoek.';
    }

    return 'Bevestig reservering';
});

const toggleReservation = () => {
    if (hasReservation.value) {
        return cancelReservation();
    }

    return makeReservation();
};

const makeReservation = async() => {
    isSubmitting.value = true;

    const { error } = await useFetchLaravel(
        `/v2.0/user/me/reservations/${props.event.id}`,
        {
            method: 'POST',
        }
    );

    if (error.value && error.value.status === 422) {
        errorMessage.value = EVENT_FULL_CAPACITY;
    } else if (error.value) {
        errorMessage.value = STATUS['500'];
    } else {
        await refreshNuxtData(USER_STORE_DATA_KEY_USER_RESERVATIONS);
    }

    isSubmitting.value = false;   
};

const cancelReservation = async() => {
    isSubmitting.value = true;

    const { error } = await useFetchLaravel(
        `/v2.0/user/me/reservations/${props.event.id}`,
        {
            method: 'DELETE',
        }
    );

    if (error.value) {
        errorMessage.value = STATUS['500'];
    } else {
        // Refresh the user, so we have the most recent reservations.
        await refreshNuxtData(USER_STORE_DATA_KEY_USER_RESERVATIONS);

        emit('submitted');

        confirm.value = false;

        useToastsStore().addMessage({
            title: 'Je hebt je reservering geannuleerd',
            message: 'Heb je je tóch bedacht? Reserveer dan opnieuw een plekje.',
            status: 'success'
        });

        eventsStore.updateMyListHome();

        setTimeout(() => {
            ++eventsStore.needsRefresh;
        }, 500);
    }

    isSubmitting.value = false;
};
</script>

<style src="./EventReservationButton.less" lang="less"></style>
